import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Subscription, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
// Services
import { ErrorHandler } from '../../shared/services/error-handler.service';
import { DataStorageService } from '../../shared/services/data-storage.service';
import { PriceService } from './price.service';
import { BookingService } from './booking.service';
// models
import { AppState } from '../models/app-state.interface';
import { LoadingStatus } from '../../shared/models/app-loader.interface';
// globals
import {
  API_PATH,
  SUBSCRIPTION_KEY,
  PAY_BY_MILES,
  GET_MILES_RANGE,
  AMEND_MCHECKOUT_STATUS,
  MILES_REFUND
} from '../../shared/globals';
// models
import { AmendMCStatusRequest, MilesRangeRequest, MilesRefundRequest, PayByMilesRequest } from '../models/miles-and-more';
import { canPayWithMiles, milesDiscount, priceWhenRedeemed, resetMiles, updateCheckoutStatus, updateMaxMilesRange, updateMilesBalance, updateMilesErrorType, updateRedeemStatus, updateSelectedMiles, updateUserLoginStatus, updateUserMiles } from '../reducers/milesAndMore.reducer';
@Injectable({
  providedIn: 'root'
})
export class MilesAndMore {
  public milesLoading$: Subject<any> = new Subject();
  public milesCheckoutStatus$: Subject<LoadingStatus> = new Subject();

  constructor(
    private _http: HttpClient,
    private _appStore: Store<AppState>,
    private _errorService: ErrorHandler,
    private _priceService: PriceService,
    private _dataStorageService: DataStorageService,
    private _bookingService: BookingService
  ) { }

  getMilesRange(request: MilesRangeRequest) {

    this.milesLoading$.next('loading');
    const headers = new HttpHeaders()
    .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    this._http.post(API_PATH + GET_MILES_RANGE, request, {
      headers: headers
    })
      .subscribe((res: any) => {
        if (res.validationErrorMessages !== null || res.validationErrors.length > 0) {
          this._errorService.handleError(res);
          this.milesLoading$.next('error');
          return;
        }

        let { maximumMiles, minimumMiles, milesBalance, serviceCardNumber, currencyRatio } = res;
        let errorType = '';
        if (milesBalance < minimumMiles) {
          errorType = 'low_miles';
        } else if (maximumMiles < minimumMiles) {
          errorType = 'low_price';
        }
        const state = this.getState(this._appStore);
        let servicePrice = state.priceReducer.price.totalPrice.majorAmount;
        if (state.bookingReducer.bookingDetails.isD2dService) {
          const bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
          if (bookingProduct === 'abc') {
            servicePrice += state.d2dReducer.inboundBooking.priceReducer.price.totalPrice.majorAmount;
          } else if (bookingProduct === 'ccd') {
            servicePrice += state.d2dReducer.outboundBooking.priceReducer.price.totalPrice.majorAmount;
          }
        }
        if (maximumMiles / currencyRatio > servicePrice) {
          maximumMiles = Math.ceil(servicePrice * currencyRatio);
        }

        if (milesBalance < maximumMiles) {
          maximumMiles = milesBalance;
        }

        this.milesLoading$.next('success');
        this._appStore.dispatch(updateMilesErrorType({ payload: errorType }));
        this._appStore.dispatch(updateUserMiles({ payload: { maximumMiles, minimumMiles, milesBalance, serviceCardNumber, currencyRatio } }));
        this._appStore.dispatch(updateUserLoginStatus({ payload: true }));
        localStorage.setItem('checkoutUrlCalled', '0');
        this._dataStorageService.saveData();
      },
        err => {
          this.milesLoading$.next('error');
          this._errorService.handleError(err);
          // console.log(err.error);
        });
  }

  payWithMiles(request: PayByMilesRequest) {
    this.milesCheckoutStatus$.next('loading');
    const headers = new HttpHeaders()
    .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    this._http.post(API_PATH + PAY_BY_MILES, request, {
      headers: headers
    })
      .subscribe((res: any) => {
        if (res.validationErrors.length > 0 ||  res.responseStatus === 'Error') {
          this._errorService.handleError(res);
          this._appStore.dispatch(canPayWithMiles({ payload: false }));
          return;
        }
        this.milesCheckoutStatus$.next('success');
        const state = this.getState(this._appStore);
        const { status, actualMilesSubmitted, actualMilesSubmittedUpsell, transactionId, checkoutUrl } = res;
        let abcMilesSubmitted = 0;
        let ccdMilesSubmitted = 0;
        let totalMilesSubmitted = actualMilesSubmitted;
        if (state.bookingReducer.bookingDetails.bookingProduct === 'abc') {
          abcMilesSubmitted = actualMilesSubmitted;
          ccdMilesSubmitted = actualMilesSubmittedUpsell;
        } else {
          abcMilesSubmitted = actualMilesSubmittedUpsell;
          ccdMilesSubmitted = actualMilesSubmitted;
        }
        if (state.bookingReducer.bookingDetails.isD2dService) {
          totalMilesSubmitted = actualMilesSubmitted + actualMilesSubmittedUpsell;
        }
        let totalBookingPrice = state.priceReducer.price.pricingResponse[0].pricingBreakdown.totalPriceMajor;
        if (state.bookingReducer.bookingDetails.isD2dService) {
          totalBookingPrice += state.bookingReducer.bookingDetails.bookingProduct === 'abc'
            ? state.d2dReducer.inboundBooking.priceReducer.price.pricingResponse[0].pricingBreakdown.totalPriceMajor
              : state.d2dReducer.outboundBooking.priceReducer.price.pricingResponse[0].pricingBreakdown.totalPriceMajor
        }
        const milesBalance = state.milesAndMoreReducer.miles.milesBalance - totalMilesSubmitted;
        const varmilesDiscount = Math.ceil(totalMilesSubmitted / state.milesAndMoreReducer.miles.currencyRatio);
        this._appStore.dispatch(updateSelectedMiles({ payload: totalMilesSubmitted }));
        this._appStore.dispatch(updateMaxMilesRange({ payload: totalMilesSubmitted }));
        this._appStore.dispatch(updateRedeemStatus({ payload: true }));
        this._appStore.dispatch(updateMilesBalance({ payload: milesBalance }));
        this._appStore.dispatch(milesDiscount({ payload: varmilesDiscount }));
        this._appStore.dispatch(priceWhenRedeemed({ payload: totalBookingPrice }));
        this._appStore.dispatch(updateCheckoutStatus({ payload: { status, abcMilesSubmitted, ccdMilesSubmitted, totalMilesSubmitted, transactionId, checkoutUrl } }));
      },
        err => {
          // console.log(err);
          this._appStore.dispatch(canPayWithMiles({ payload: false }));
          this.milesCheckoutStatus$.next('error');
          this._errorService.handleError(err);
        });
  }

  amendMcheckoutStatus(request: AmendMCStatusRequest) {
    const headers = new HttpHeaders()
    .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    this._http.post(API_PATH + AMEND_MCHECKOUT_STATUS, request, {
      headers: headers
    })
      .subscribe((res: any) => {
        if (res.validationErrors.length > 0 || res.responseStatus === 'Error') {
          this._errorService.handleError(res);
          return;
        }
      },
        err => {
          this._errorService.handleError(err);
          this._appStore.dispatch(canPayWithMiles({ payload: false }));
          this._priceService.getPrice();
        });
  }

  amendMcheckoutStatusPromise(request: AmendMCStatusRequest) {
    const promise = new Promise((resolve, reject) => {
      const headers = new HttpHeaders()
        .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
      this._http.post(API_PATH + AMEND_MCHECKOUT_STATUS, request, {
        headers: headers
      })
        .toPromise()
        .then((res: any) => {
          if (res.validationErrors.length > 0 || res.responseStatus === 'Error') {
            this._errorService.handleError(res);
            return;
          }
          resolve(res);
        })
        .catch(err => {
          this._errorService.handleError(err);
          this._appStore.dispatch(canPayWithMiles({ payload: false }));
          resolve('error');
        })
    });
    return promise;
  }

  milesRefund(request: MilesRefundRequest) {
    const promise = new Promise((resolve, reject) => {
      const headers = new HttpHeaders()
          .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
      this._http.post(API_PATH + MILES_REFUND, request, {
        headers: headers
      }).toPromise()
        .then((res: any) => {
          if (res.validationErrors.length > 0 || res.responseStatus === 'Error') {
            this._errorService.handleError(res);
          }
          resolve(res);
        })
        .catch(err => {
          this._errorService.handleError(err);
          resolve('error');
        });
    });

    return promise;
  }

  async milesLoginRedirect(milesLoginUrl, callback) {
    const state = this.getState(this._appStore);
    const bookingReference = state.bookingReducer.bookingDetails.bookingReference;
    const transactionId = state.milesAndMoreReducer.milesVerified?.id;
    if (!transactionId) {
      this._errorService.handleCustomError({
        text: 'Miles cannot be updated',
        sticky: false
      });
      return;
    }
    try {
      const milesRedunded = await this.milesRefund({
        bookingReference: bookingReference,
        transactionId: transactionId
      });
      if (milesRedunded !== 'error') {
        this._appStore.dispatch(resetMiles());
      }
      const dataResponse = await this._dataStorageService.saveDataPromise()
      if (dataResponse && milesRedunded !== 'error') {
        const milesLink = `${milesLoginUrl}&lang=${state.copyReducer.languageCode}&redirect_uri=${callback}`
        const anchorTag = document.createElement('a');
        anchorTag.setAttribute('href', milesLink);
        anchorTag.hidden = true;
        document.body.appendChild(anchorTag);
        anchorTag.click();
      } else {
        this._errorService.handleCustomError({
          text: 'Miles login failed, please try again',
          sticky: false
        });
      }
    } catch (e) {
     // console.log(e);
    }
  }

  getState(store: Store<AppState>): AppState {
    let state;
    store.pipe(take(1))
      .subscribe(s => state = s);
    return state;
  }
}
